/**
 * Character Counting Utilities
 *
 * Provides accurate character counting for social media platforms.
 * Uses Intl.Segmenter to count grapheme clusters (what users see as characters)
 * and applies platform-specific counting rules.
 *
 * Key considerations:
 * - JavaScript string.length counts UTF-16 code units, not visible characters
 * - Emojis like "face" are 2 code units but 1 visible character
 * - Compound emojis like "family" can be 11+ code units but 1 visible character
 * - Twitter/X counts emojis as 2 characters each
 * - Most other platforms count grapheme clusters (1 emoji = 1 character)
 *
 * @example
 * ```ts
 * import { countCharacters, countCharactersForPlatform } from '@/utils/characterCount';
 *
 * // Basic counting (grapheme clusters)
 * countCharacters("Hello face") // 7 (6 letters + 1 emoji)
 *
 * // Platform-specific counting
 * countCharactersForPlatform("Hello face", "twitter") // 8 (6 letters + 2 for emoji)
 * countCharactersForPlatform("Hello face", "linkedin") // 7 (standard counting)
 * ```
 */

/**
 * Regular expression to detect emoji characters
 * Uses Unicode property escapes for accurate emoji detection
 */
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

/**
 * Check if a string segment contains an emoji
 *
 * @param segment - A single grapheme cluster
 * @returns true if the segment contains an emoji
 */
function isEmoji(segment: string): boolean {
  return EMOJI_REGEX.test(segment);
}

/**
 * Type declaration for Intl.Segmenter (ES2022+)
 * Some TypeScript configurations may not include this by default
 */
interface SegmenterSegment {
  segment: string;
  index: number;
  input: string;
}

interface IntlSegmenter {
  segment(input: string): Iterable<SegmenterSegment>;
}

interface IntlSegmenterConstructor {
  new (locales?: string | string[], options?: { granularity?: 'grapheme' | 'word' | 'sentence' }): IntlSegmenter;
}

/**
 * Check if Intl.Segmenter is available (modern browsers)
 * Intl.Segmenter is supported in Chrome 87+, Edge 87+, Firefox 125+, Safari 15.4+
 */
const hasSegmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl;

/**
 * Lazily-constructed, memoized `Intl.Segmenter` instance (grapheme granularity).
 *
 * `Intl.Segmenter` construction loads ICU segmentation data and can be very
 * expensive (observed up to ~1.2s cumulatively on slower devices for a long
 * caption counted across multiple platforms on every keystroke). We only ever
 * need ONE grapheme segmenter for the lifetime of the app, so we build it once
 * and reuse it for every call instead of constructing a new one per call.
 */
let graphemeSegmenter: IntlSegmenter | null = null;

function getGraphemeSegmenter(): IntlSegmenter {
  if (!graphemeSegmenter) {
    // Cast to access Segmenter which may not be in all TypeScript lib definitions
    const SegmenterClass = (Intl as unknown as { Segmenter: IntlSegmenterConstructor }).Segmenter;
    graphemeSegmenter = new SegmenterClass('en', { granularity: 'grapheme' });
  }
  return graphemeSegmenter;
}

/**
 * Last-value cache for `getGraphemeSegments`.
 *
 * The same caption is segmented repeatedly - once per selected platform, on
 * every render/keystroke (see `checkLimitsWithCounting` /
 * `countCharactersForPlatforms`). Caching the segmentation result for the most
 * recently seen input text avoids redundant re-segmentation of the same
 * string within a single render pass.
 *
 * The returned array is read-only for all callers (`.length`, iteration for
 * `isEmoji`) and is never mutated, so it is safe to hand out the shared
 * cached reference.
 */
let lastSegmentedText: string | null = null;
let lastSegments: string[] = [];

/**
 * Get grapheme segments from text
 *
 * @param text - The text to segment
 * @returns Array of grapheme segments
 */
function getGraphemeSegments(text: string): string[] {
  if (text === lastSegmentedText) {
    return lastSegments;
  }

  let segments: string[];

  if (hasSegmenter) {
    // Modern approach: Use Intl.Segmenter for accurate grapheme clustering
    const segmenter = getGraphemeSegmenter();
    segments = [...segmenter.segment(text)].map((s) => s.segment);
  } else {
    // Fallback: Use spread operator for basic grapheme handling
    // This handles simple emojis but not all compound emojis perfectly
    segments = [...text];
  }

  lastSegmentedText = text;
  lastSegments = segments;

  return segments;
}

/**
 * Count characters using grapheme clusters (what users see as characters)
 *
 * This counts each visible character as 1, regardless of how many
 * UTF-16 code units it uses internally.
 *
 * @param text - The text to count
 * @returns Number of visible characters (grapheme clusters)
 *
 * @example
 * ```ts
 * countCharacters("Hello") // 5
 * countCharacters("Hello face") // 7 (face emoji = 1)
 * countCharacters("family") // 1 (family emoji = 1, despite being 11+ code units)
 * ```
 */
export function countCharacters(text: string): number {
  if (!text) return 0;
  return getGraphemeSegments(text).length;
}

/**
 * Platform-specific character counting rules
 *
 * Different platforms count characters differently, especially for emojis:
 * - Twitter/X: Each emoji counts as 2 characters (weighted)
 * - LinkedIn, Facebook, Instagram: Standard grapheme counting (1 emoji = 1 char)
 * - Bluesky: Counts bytes, but we approximate with graphemes + emoji weight
 */
interface PlatformCountingRule {
  /** Weight multiplier for emoji characters */
  emojiWeight: number;
  /** Description of the counting rule */
  description: string;
}

const PLATFORM_COUNTING_RULES: Record<string, PlatformCountingRule> = {
  twitter: {
    emojiWeight: 2,
    description: 'Twitter counts each emoji as 2 characters',
  },
  x: {
    emojiWeight: 2,
    description: 'X (Twitter) counts each emoji as 2 characters',
  },
  // Most platforms use standard grapheme counting
  linkedin: {
    emojiWeight: 1,
    description: 'LinkedIn counts emojis as 1 character each',
  },
  facebook: {
    emojiWeight: 1,
    description: 'Facebook counts emojis as 1 character each',
  },
  instagram: {
    emojiWeight: 1,
    description: 'Instagram counts emojis as 1 character each',
  },
  tiktok: {
    emojiWeight: 1,
    description: 'TikTok counts emojis as 1 character each',
  },
  youtube: {
    emojiWeight: 1,
    description: 'YouTube counts emojis as 1 character each',
  },
  pinterest: {
    emojiWeight: 1,
    description: 'Pinterest counts emojis as 1 character each',
  },
  threads: {
    emojiWeight: 1,
    description: 'Threads counts emojis as 1 character each',
  },
  bluesky: {
    emojiWeight: 1,
    description: 'Bluesky uses UTF-8 byte counting, approximated here',
  },
  google_my_business: {
    emojiWeight: 1,
    description: 'Google Business counts emojis as 1 character each',
  },
};

/**
 * Get the counting rule for a platform
 *
 * @param platform - Platform key (e.g., 'twitter', 'linkedin')
 * @returns Platform counting rule or default rule
 */
export function getPlatformCountingRule(platform: string): PlatformCountingRule {
  const normalizedPlatform = platform.toLowerCase();
  return (
    PLATFORM_COUNTING_RULES[normalizedPlatform] || {
      emojiWeight: 1,
      description: 'Standard grapheme counting',
    }
  );
}

/**
 * Count characters for a specific platform
 *
 * Applies platform-specific counting rules, such as Twitter's
 * emoji weighting (2 characters per emoji).
 *
 * @param text - The text to count
 * @param platform - Platform key (e.g., 'twitter', 'linkedin')
 * @returns Character count according to platform rules
 *
 * @example
 * ```ts
 * // Twitter counts emojis as 2 characters
 * countCharactersForPlatform("Hi face", "twitter") // 5 (2 + 1 space + 2 for emoji)
 *
 * // LinkedIn uses standard counting
 * countCharactersForPlatform("Hi face", "linkedin") // 4 (2 + 1 space + 1 for emoji)
 * ```
 */
export function countCharactersForPlatform(text: string, platform?: string): number {
  if (!text) return 0;

  const segments = getGraphemeSegments(text);

  // If no platform specified, use standard grapheme counting
  if (!platform) {
    return segments.length;
  }

  const rule = getPlatformCountingRule(platform);

  // If emoji weight is 1, just return segment count
  if (rule.emojiWeight === 1) {
    return segments.length;
  }

  // Apply emoji weighting
  let count = 0;
  for (const segment of segments) {
    if (isEmoji(segment)) {
      count += rule.emojiWeight;
    } else {
      count += 1;
    }
  }

  return count;
}

/**
 * Count characters for multiple platforms and return the maximum count
 *
 * Useful when posting to multiple platforms with different counting rules.
 * Returns the highest character count among all platforms.
 *
 * @param text - The text to count
 * @param platforms - Array of platform keys
 * @returns Maximum character count across all platforms
 *
 * @example
 * ```ts
 * // If posting "Hi face" to both Twitter and LinkedIn
 * countCharactersForPlatforms("Hi face", ["twitter", "linkedin"])
 * // Returns 5 (Twitter's count, which is higher due to emoji weighting)
 * ```
 */
export function countCharactersForPlatforms(text: string, platforms: string[]): number {
  if (!text || platforms.length === 0) {
    return countCharacters(text);
  }

  const counts = platforms.map((platform) => countCharactersForPlatform(text, platform));
  return Math.max(...counts);
}

/**
 * Get detailed character count breakdown for all platforms
 *
 * @param text - The text to count
 * @param platforms - Array of platform keys
 * @returns Object with count for each platform
 *
 * @example
 * ```ts
 * getCharacterCountBreakdown("Hi face", ["twitter", "linkedin"])
 * // Returns { twitter: 5, linkedin: 4, standard: 4 }
 * ```
 */
export function getCharacterCountBreakdown(
  text: string,
  platforms: string[]
): Record<string, number> {
  const breakdown: Record<string, number> = {
    standard: countCharacters(text),
  };

  for (const platform of platforms) {
    breakdown[platform.toLowerCase()] = countCharactersForPlatform(text, platform);
  }

  return breakdown;
}

/**
 * Count words in text
 *
 * @param text - The text to count words in
 * @returns Number of words
 */
export function countWords(text: string): number {
  if (!text || !text.trim()) return 0;
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

/**
 * Get both character and word counts
 *
 * @param text - The text to analyze
 * @param platform - Optional platform for character counting
 * @returns Object with character and word counts
 */
export function getTextStats(
  text: string,
  platform?: string
): {
  characters: number;
  words: number;
  graphemes: number;
  codeUnits: number;
} {
  return {
    characters: countCharactersForPlatform(text, platform),
    words: countWords(text),
    graphemes: countCharacters(text),
    codeUnits: text.length,
  };
}

export default {
  countCharacters,
  countCharactersForPlatform,
  countCharactersForPlatforms,
  getCharacterCountBreakdown,
  countWords,
  getTextStats,
  getPlatformCountingRule,
};
